import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { PmOneLoginTokenService } from './pm-onelogin-token.service';

/**
 * OneLogin = JWS RS256 (3 parts, unencrypted header). Mirrors the check used in
 * ui-angular-subscriptionportal's auth.guard.ts to reject anything that isn't a genuine OneLogin
 * access_token before trusting it.
 */
function isOneLoginAccessToken(token: string | null | undefined): boolean {
  if (!token) {
    return false;
  }
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }
  try {
    const headerJson = parts[0].replace(/-/g, '+').replace(/_/g, '/');
    const pad = 4 - (headerJson.length % 4);
    const padded = pad === 4 ? headerJson : headerJson + '='.repeat(pad);
    const header = JSON.parse(atob(padded)) as { alg?: string; enc?: string };
    return !header.enc && (!header.alg || header.alg === 'RS256');
  } catch {
    return false;
  }
}

/**
 * OneLogin/APIM pilot login guard (mirrors ui-angular-subscriptionportal's proven auth.guard.ts).
 * Wired into every route via app.routes.ts's canActivateChild.
 */
@Injectable({ providedIn: 'root' })
export class PmOneLoginAuthGuard implements CanActivate, CanActivateChild {
  // Optional: only has a provider when provideAuth() ran (environment.oneLoginAuthority/
  // oneLoginClientId set — Dev only today). Beta/Prod construct this guard too (it's wired into
  // every route in app.routes.ts), so a required inject() here would crash app bootstrap there.
  private readonly oidcSecurityService = inject(OidcSecurityService, { optional: true });
  private readonly tokenService = inject(PmOneLoginTokenService);
  private readonly router = inject(Router);

  /**
   * True while checkAuth() is running (including the moment right before the browser navigates
   * away to OneLogin) — bound in AppComponent to show a full-page loading state, since the router
   * renders nothing for the target route until this guard resolves.
   */
  readonly checking = signal(false);

  canActivateChild(next: ActivatedRouteSnapshot): Observable<boolean> {
    return this.canActivate(next);
  }

  canActivate(_route: ActivatedRouteSnapshot): Observable<boolean> {
    if (!this.oidcSecurityService) {
      // OneLogin isn't configured in this environment (Beta/Prod today) — let routes through
      // unchanged, same as before this guard existed.
      return of(true);
    }

    this.checking.set(true);
    return this.oidcSecurityService.checkAuth().pipe(
      take(1),
      switchMap((loginResponse) => {
        const token =
          loginResponse.accessToken || (loginResponse as { access_token?: string }).access_token;

        if (!loginResponse.isAuthenticated || !isOneLoginAccessToken(token)) {
          this.tokenService.clear();
          this.login();
          // Stays true — the browser is about to navigate away to OneLogin, so the loading
          // state should persist right up until that redirect actually happens.
          return of(false);
        }

        this.tokenService.setAccessToken(token ?? null);
        return of(true);
      }),
      catchError(() => {
        this.tokenService.clear();
        this.login();
        return of(false);
      }),
      finalize(() => {
        // Only clear it on the "let the route through" path — the redirect-to-OneLogin path
        // intentionally leaves it true (see above) until the page actually navigates away.
        if (this.tokenService.hasToken()) {
          this.checking.set(false);
        }
      })
    );
  }

  login(): void {
    this.oidcSecurityService?.authorize();
  }

  logout(): Observable<unknown> {
    if (!this.oidcSecurityService) {
      this.tokenService.clear();
      return of(null);
    }
    return this.oidcSecurityService.logoff().pipe(
      take(1),
      map(() => this.tokenService.clear())
    );
  }
}
